import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SEOHead from '@/components/base/SEOHead';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const comics = getComicsByCategory(slug || '');

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-4xl mb-4">😕</p>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">分类未找到</h2>
            <p className="text-sm text-foreground-500">该漫画分类不存在或已被移除</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name}韩漫 - 韩漫在线观看`,
    description: category.description,
    url: `http://zbpost.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网站',
      url: 'http://zbpost.cn/',
    },
    about: {
      '@type': 'Thing',
      name: category.name,
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name}韩漫 - 韩漫在线观看`}
        description={`韩漫在线观看${category.name}分类。${category.description}。免费下拉式阅读，智能预加载，极速流畅体验。收录${category.count}部${category.name}韩漫作品。`}
        keywords={`${category.name}韩漫, 韩漫在线观看, ${category.name}漫画, 韩漫网站`}
        canonicalUrl={`/category/${slug}`}
        schema={collectionSchema}
      />

      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="py-6 md:py-8">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}韩漫
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 pb-14">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-4xl mb-3">📚</p>
              <p className="text-foreground-500">该分类暂无漫画作品</p>
            </div>
          )}

          <div className="pb-10">
            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">浏览其他分类</h3>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}